import { createMultiStyleConfigHelpers, ThemingProps } from '@chakra-ui/react'
import { asChakraVariable, themeColorSectionKeys } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { Radii, Spacing } from '~/types/ThemeTypes'
import {
  createCustomStyleContext,
  useCustomMultiStyleConfig,
} from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

export enum MangoAlertVariant {
  Success = 'success',
  Error = 'error',
  Loading = 'loading',
  Info = 'info',
  Warning = 'warning',
}

const { MangoAlert } = themeColorSectionKeys.shared

const mangoAlertStyleParts = [
  'container',
  'contentContainer',
  'textContainer',
  'title',
  'description',
  'icon',
  'circleProgress',
  'closeButton',
] as const

const helpers = createMultiStyleConfigHelpers(mangoAlertStyleParts)
export const MangoAlertStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      color: MangoAlert.FontColor,
      minWidth: '12.5rem',
      maxWidth: '22.5rem',
      borderRadius: Radii.lg,
      py: Spacing._3,
      px: Spacing._4,
      userSelect: 'none',
      boxShadow: `0px 4px 12px ${asChakraVariable(MangoAlert.BoxShadow)}`,
    },
    contentContainer: {
      gap: Spacing._3,
    },
    textContainer: {
      gap: 0,
      alignItems: 'start',
    },
    title: {
      textStyle: textStyleKeys.Body1_Bold,
    },
    description: {
      textStyle: textStyleKeys.Body1,
    },
    icon: {
      color: MangoAlert.Icon,
      height: '1.5rem',
      width: '1.5rem',
    },
    closeButton: {
      position: 'absolute',
      right: '2',
      top: '2',
    },
    circleProgress: {
      height: '2.0rem',
      width: '6px',
      backgroundColor: MangoAlert.CircularProgress.TrackColor,
      color: MangoAlert.CircularProgress.ProgressColor,
    },
  },
  variants: {
    [MangoAlertVariant.Success]: {
      container: {
        backgroundColor: MangoAlert.Background.Success,
      },
    },
    [MangoAlertVariant.Error]: {
      container: {
        backgroundColor: MangoAlert.Background.Error,
      },
    },
    [MangoAlertVariant.Loading]: {
      container: {
        backgroundColor: MangoAlert.Background.Loading,
      },
    },
    [MangoAlertVariant.Info]: {
      container: {
        backgroundColor: MangoAlert.Background.Info,
      },
    },
    [MangoAlertVariant.Warning]: {
      container: {
        backgroundColor: MangoAlert.Background.Warning,
      },
    },
  },
})

const themeKey = getName({ MangoAlertStyle })
export const useMangoAlertStyles = (props?: ThemingProps) =>
  useCustomMultiStyleConfig<typeof mangoAlertStyleParts>(themeKey, props)

export const createMangoAlertStyleContext = () =>
  createCustomStyleContext<typeof mangoAlertStyleParts>(themeKey)
